// Auth state — replaces the localStorage-juggling in the old auth.js / app.js.
//
// Zustand's `persist` middleware mirrors the previous manual localStorage keys
// (pwa-chat-token, pwa-chat-user-id, pwa-chat-display-name) into a single
// persisted slice, so a reload restores the session exactly as before.

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { createPersistentStorage } from '../lib/persistentStorage'

interface Session {
  accessToken: string
  userId: string
  displayName: string
}

interface AuthState {
  sessionGeneration: number
  accessToken: string | null
  userId: string | null
  displayName: string | null
  setSession: (session: Session) => void
  setAccessToken: (token: string) => void
  setDisplayName: (displayName: string) => void
  logout: () => void
  isAuthenticated: () => boolean
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      sessionGeneration: 0,
      accessToken: null,
      userId: null,
      displayName: null,

      setSession: ({ accessToken, userId, displayName }) => set((state) => ({ accessToken, userId, displayName, sessionGeneration: state.sessionGeneration + 1 })),

      // Sliding JWT refresh (bd-9d2): backend may stamp X-Refreshed-Token on
      // any 2xx response when the current token is near expiry. Update only
      // the token — keep user_id / displayName intact.
      setAccessToken: (token) => set({ accessToken: token }),

      // Sync the display name from the server /profile (original _handlePostAuth
      // wrote the server display_name back to the sidebar/localStorage).
      setDisplayName: (displayName) => set({ displayName }),

      logout: () => set((state) => ({ accessToken: null, userId: null, displayName: null, sessionGeneration: state.sessionGeneration + 1 })),

      isAuthenticated: () => Boolean(get().accessToken),
    }),
    {
      name: 'humanteq-auth',
      storage: createPersistentStorage(),
    }
  )
)
